import {isMacOS} from '@github-ui/get-os'
import type {ContextCrumb} from '@github-ui/global-navigation-types'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {useNavigableRef} from '@github-ui/navigable-ref'
import {preserveStaffNavigationParams} from '@github-ui/react-app-navigation/staff-navigation-params'
import {Link, PREVENT_AUTOFOCUS_KEY} from '@github-ui/react-router'
import {CommandIconButton} from '@github-ui/ui-commands'
import {LockIcon, RepoForkedIcon, RepoIcon, RepoLockedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Breadcrumbs} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'

import {getRepositoryContextCrumb} from '../../context-region/context-crumbs'
import {getDisplayedContext} from '../../context-region/navigate-with-context-refresh'
import type {GlobalNavigationContext} from '../../context-region/refresh-global-navigation'
import {useNavigateWithContextRefresh} from '../../context-region/use-navigate-with-context-refresh'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {NavOcticon} from '../../utils/octicons'
import styles from './styles.module.css'
import {type Repository, useRepositorySuggestions} from './use-repository-suggestions'

function shouldOpenInNewTab(event: React.MouseEvent | React.KeyboardEvent): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  const isPlatformModifierClick = isMacOS() ? event.metaKey : event.ctrlKey
  const isMiddleClick = (event as React.MouseEvent).button === 1
  return isPlatformModifierClick || isMiddleClick
}

export interface RepoPickerProps {
  repositoryCrumb: ContextCrumb
  ownerCrumb: ContextCrumb
  currentPath?: string
  selectedLink?: string
  onCrumbClick?: () => void
}

export function RepoPicker({repositoryCrumb, ownerCrumb, currentPath, selectedLink, onCrumbClick}: RepoPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const {sendItemClick} = useGlobalNavAnalytics('REPO_PICKER')
  const navigateWithContextRefresh = useNavigateWithContextRefresh()
  const isCurrentRepoPrivate = repositoryCrumb.octicon === 'lock'
  const isCurrentRepoForked = repositoryCrumb.octicon === 'repo-forked'

  // used to track the last action event to detect modifier keys for opening suggestions in new tabs
  const lastActionEventRef = useRef<React.MouseEvent | React.KeyboardEvent | null>(null)

  // Add navigable ref support for Alt+F6 / Alt+Shift+F6 navigation
  const navigableRef = useNavigableRef<HTMLButtonElement>()

  const {repositories, isLoading, searchQuery, handleFilterChange} = useRepositorySuggestions({
    owner: ownerCrumb.label,
    enabled: isOpen || isHovering || isFocused,
    currentRepositoryName: repositoryCrumb.label,
    currentRepositoryIconType: repositoryCrumb.octicon || 'repo',
  })

  const convertToItemProps = useCallback(
    (repo: Repository) => {
      return {
        id: repo.nameWithOwner,
        text: repo.name,
        source: repo,
        leadingVisual: () =>
          repo.iconType === 'lock' ? (
            <RepoLockedIcon size={16} />
          ) : (
            <NavOcticon name={repo.iconType} size={16} fallback={<RepoIcon size={16} />} />
          ),
        onAction: (_itemProps: unknown, event: React.MouseEvent | React.KeyboardEvent) => {
          // store the event so handleSelectionChange can check modifier keys
          lastActionEventRef.current = event

          // we need to handle opening new tabs in the click handler to avoid popup blockers
          if (shouldOpenInNewTab(event)) {
            event.preventDefault()
            sendItemClick('REPO_SELECTED')
            window.open(`/${repo.nameWithOwner}`, '_blank')
          }
        },
      }
    },
    [sendItemClick],
  )

  const getItemKey = (repo: Repository) => repo.nameWithOwner

  const currentRepoHref = repositoryCrumb.href || repositoryCrumb.path || `/${repositoryCrumb.label}`
  const currentRepoName = repositoryCrumb.label.split('/').pop() || repositoryCrumb.label
  const currentRepoNwo = `${ownerCrumb.label}/${currentRepoName}`

  const handleSelectionChange = useCallback(
    (items: Repository[]) => {
      const selectedRepo = items[0]
      if (!selectedRepo) return

      // if the link was already opened in a new tab, do nothing
      const openedInNewTab = lastActionEventRef.current && shouldOpenInNewTab(lastActionEventRef.current)
      lastActionEventRef.current = null
      if (openedInNewTab) return

      sendItemClick('REPO_SELECTED')
      const destination = `/${selectedRepo.nameWithOwner}${preserveStaffNavigationParams('')}`
      const destinationContext: GlobalNavigationContext = {
        ...getRepositoryContextCrumb(selectedRepo.nameWithOwner),
        path: destination,
        selectedLink: 'repo_source',
      }
      navigateWithContextRefresh({
        appName: 'code-view',
        destinationContext,
        sourceContext: getDisplayedContext(getRepositoryContextCrumb(currentRepoNwo), {currentPath, selectedLink}),
        state: {
          // Navigation autofocus targets the first `react-app h1`, which on the repository overview is often
          // the README heading far down the page, so focusing it scrolls the page.
          [PREVENT_AUTOFOCUS_KEY]: true,
        },
      })
    },
    [currentPath, currentRepoNwo, navigateWithContextRefresh, selectedLink, sendItemClick],
  )

  const initialSelectedItems = useMemo(() => {
    const currentRepo = repositories.find(repo => repo.name === repositoryCrumb.label)
    return currentRepo ? [currentRepo] : []
  }, [repositories, repositoryCrumb.label])

  return (
    <>
      <Breadcrumbs.Item
        as={Link}
        to={currentRepoHref}
        className={clsx(styles.contextCrumb, styles.contextCrumbLast, styles.repoPickerCrumb)}
        onClick={onCrumbClick}
      >
        {isCurrentRepoForked ? (
          <RepoForkedIcon className={styles.crumbIcon} />
        ) : isCurrentRepoPrivate ? (
          <LockIcon className={styles.crumbIcon} />
        ) : null}
        {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
        {currentRepoName}
      </Breadcrumbs.Item>
      <ItemPicker<Repository>
        renderAnchor={({'aria-labelledby': ariaLabelledBy, 'aria-label': ariaLabel, ...anchorProps}) => (
          <CommandIconButton
            {...anchorProps}
            icon={TriangleDownIcon}
            variant="invisible"
            size="small"
            aria-label="Switch repository"
            commandId="global-navigation:open-repo-picker"
            className={clsx(styles.contextCrumb, styles.repoPickerDropdownButton)}
            onMouseEnter={() => setIsHovering(true)}
            onFocus={() => setIsFocused(true)}
          />
        )}
        items={repositories}
        initialSelectedItems={initialSelectedItems}
        placeholderText="Search repositories"
        selectionVariant="single"
        loading={isLoading}
        filterItems={handleFilterChange}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        onSelectionChange={handleSelectionChange}
        onOpen={() => {
          sendItemClick('REPO_PICKER_TOGGLE')
          setIsOpen(true)
        }}
        onClose={() => setIsOpen(false)}
        title="Switch repository"
        width="medium"
        improvedNoMatchAccessibility
        noMatchMessage={
          isLoading
            ? {variant: 'empty' as const, title: 'Loading repositories...', body: ''}
            : repositories.length === 0 && searchQuery
              ? {variant: 'empty' as const, title: 'No repositories found', body: ''}
              : undefined
        }
        keybindingCommandId="global-navigation:open-repo-picker"
        selectPanelRef={navigableRef}
        setInitialFocus
      />
    </>
  )
}
